import {
  ACCOUNT_SIZE,
  type Account,
  MINT_SIZE,
  TOKEN_PROGRAM_ID,
  unpackAccount,
  unpackMint,
} from '@solana/spl-token';
import type { AccountInfo, PublicKey } from '@solana/web3.js';
import { USDC_DECIMALS } from '../constants/numeric.js';
import { CP_SWAP_PROGRAM_ID } from '../constants/programs.js';
import {
  deriveAmmAuthorityPdaWithBump,
  deriveAmmConfigPda,
  deriveAmmObservationPda,
  deriveAmmPoolStatePda,
  deriveAmmPoolVaultPda,
  sortMintPair,
} from '../pda/cp-swap.js';
import type { AmmConfigAccount, PoolStateAccount } from './cp-swap-types.js';

export type CpSwapValidationReason =
  | 'unexpected_program'
  | 'amm_config_pda'
  | 'amm_config_index'
  | 'mint_pair'
  | 'pool_pda'
  | 'vault_pda'
  | 'observation_pda'
  | 'observation_account_data'
  | 'observation_pool_binding'
  | 'authority_bump'
  | 'token_program'
  | 'token_account_program'
  | 'token_account_layout'
  | 'vault_mint'
  | 'vault_authority'
  | 'vault_uninitialized'
  | 'vault_frozen'
  | 'mint_decimals'
  | 'pool_mint_decimals'
  | 'amount_in_out_of_range'
  | 'min_amount_out_out_of_range'
  | 'pool_identity_changed';

export class CpSwapValidationError extends Error {
  readonly code = 'CP_SWAP_VALIDATION_FAILED';

  constructor(readonly reason: CpSwapValidationReason) {
    super(`cp-swap validation failed: ${reason}`);
    this.name = 'CpSwapValidationError';
  }
}

interface PoolIdentityInput {
  poolState: PublicKey;
  pool: PoolStateAccount;
  expectedQuoteMint: PublicKey;
  expectedOutcomeMint: PublicKey;
}

export interface PoolTokenIndexes {
  quoteTokenIndex: 0 | 1;
  outcomeTokenIndex: 0 | 1;
}

interface AtomicTokenAccountsInput {
  pool: PoolStateAccount;
  token0VaultInfo: AccountInfo<Buffer>;
  token1VaultInfo: AccountInfo<Buffer>;
  token0MintInfo: AccountInfo<Buffer>;
  token1MintInfo: AccountInfo<Buffer>;
}

export interface ValidatedVaultAccounts {
  token0: Account;
  token1: Account;
}

export function requireCanonicalCpSwapProgram(programId: PublicKey): void {
  if (!programId.equals(CP_SWAP_PROGRAM_ID)) reject('unexpected_program');
}

export function validateCanonicalAmmConfig(address: PublicKey, config: AmmConfigAccount): void {
  requireCanonicalAmmConfigAddress(address);
  if (config.index !== 0) reject('amm_config_index');
}

export function requireCanonicalAmmConfigAddress(address: PublicKey): void {
  if (!address.equals(deriveAmmConfigPda(CP_SWAP_PROGRAM_ID))) reject('amm_config_pda');
}

export function validateCanonicalPoolIdentity(input: PoolIdentityInput): PoolTokenIndexes {
  const { pool, poolState, expectedQuoteMint, expectedOutcomeMint } = input;
  if (expectedQuoteMint.equals(expectedOutcomeMint)) reject('mint_pair');
  const [expectedMint0, expectedMint1] = sortMintPair(expectedQuoteMint, expectedOutcomeMint);
  if (!pool.token0Mint.equals(expectedMint0) || !pool.token1Mint.equals(expectedMint1)) {
    reject('mint_pair');
  }
  if (!pool.ammConfig.equals(deriveAmmConfigPda(CP_SWAP_PROGRAM_ID))) reject('amm_config_pda');
  if (
    !poolState.equals(
      deriveAmmPoolStatePda(pool.ammConfig, expectedMint0, expectedMint1, CP_SWAP_PROGRAM_ID),
    )
  ) {
    reject('pool_pda');
  }
  if (
    !pool.token0Vault.equals(deriveAmmPoolVaultPda(poolState, expectedMint0, CP_SWAP_PROGRAM_ID)) ||
    !pool.token1Vault.equals(deriveAmmPoolVaultPda(poolState, expectedMint1, CP_SWAP_PROGRAM_ID))
  ) {
    reject('vault_pda');
  }
  if (!pool.observationKey.equals(deriveAmmObservationPda(poolState, CP_SWAP_PROGRAM_ID))) {
    reject('observation_pda');
  }
  const [, expectedAuthorityBump] = deriveAmmAuthorityPdaWithBump(CP_SWAP_PROGRAM_ID);
  if (pool.authBump !== expectedAuthorityBump) reject('authority_bump');
  if (
    !pool.token0Program.equals(TOKEN_PROGRAM_ID) ||
    !pool.token1Program.equals(TOKEN_PROGRAM_ID)
  ) {
    reject('token_program');
  }
  const quoteTokenIndex: 0 | 1 = expectedQuoteMint.equals(expectedMint0) ? 0 : 1;
  return { quoteTokenIndex, outcomeTokenIndex: quoteTokenIndex === 0 ? 1 : 0 };
}

export function validateAtomicTokenAccounts(
  input: AtomicTokenAccountsInput,
): ValidatedVaultAccounts {
  const { pool, token0VaultInfo, token1VaultInfo, token0MintInfo, token1MintInfo } = input;
  const infos = [token0VaultInfo, token1VaultInfo, token0MintInfo, token1MintInfo];
  if (infos.some((info) => !info.owner.equals(TOKEN_PROGRAM_ID))) {
    reject('token_account_program');
  }
  if (
    token0VaultInfo.data.length !== ACCOUNT_SIZE ||
    token1VaultInfo.data.length !== ACCOUNT_SIZE ||
    token0MintInfo.data.length !== MINT_SIZE ||
    token1MintInfo.data.length !== MINT_SIZE
  ) {
    reject('token_account_layout');
  }

  const token0Mint = unpackMint(pool.token0Mint, token0MintInfo, TOKEN_PROGRAM_ID);
  const token1Mint = unpackMint(pool.token1Mint, token1MintInfo, TOKEN_PROGRAM_ID);
  if (token0Mint.decimals !== USDC_DECIMALS || token1Mint.decimals !== USDC_DECIMALS) {
    reject('mint_decimals');
  }
  if (pool.mint0Decimals !== token0Mint.decimals || pool.mint1Decimals !== token1Mint.decimals) {
    reject('pool_mint_decimals');
  }

  const token0 = unpackAccount(pool.token0Vault, token0VaultInfo, TOKEN_PROGRAM_ID);
  const token1 = unpackAccount(pool.token1Vault, token1VaultInfo, TOKEN_PROGRAM_ID);
  if (!token0.isInitialized || !token1.isInitialized) reject('vault_uninitialized');
  if (token0.isFrozen || token1.isFrozen) reject('vault_frozen');
  if (!token0.mint.equals(pool.token0Mint) || !token1.mint.equals(pool.token1Mint)) {
    reject('vault_mint');
  }
  const [authority] = deriveAmmAuthorityPdaWithBump(CP_SWAP_PROGRAM_ID);
  if (!token0.owner.equals(authority) || !token1.owner.equals(authority)) {
    reject('vault_authority');
  }
  return { token0, token1 };
}

export function samePoolIdentity(left: PoolStateAccount, right: PoolStateAccount): boolean {
  return (
    left.ammConfig.equals(right.ammConfig) &&
    left.poolCreator.equals(right.poolCreator) &&
    left.token0Vault.equals(right.token0Vault) &&
    left.token1Vault.equals(right.token1Vault) &&
    left.token0Mint.equals(right.token0Mint) &&
    left.token1Mint.equals(right.token1Mint) &&
    left.token0Program.equals(right.token0Program) &&
    left.token1Program.equals(right.token1Program) &&
    left.observationKey.equals(right.observationKey) &&
    left.authBump === right.authBump &&
    left.mint0Decimals === right.mint0Decimals &&
    left.mint1Decimals === right.mint1Decimals
  );
}

export function rejectPoolIdentityChange(): never {
  return reject('pool_identity_changed');
}

export function rejectInvalidObservationAccount(): never {
  return reject('observation_account_data');
}

export function requireObservationPoolBinding(
  poolState: PublicKey,
  observationPoolId: PublicKey,
): void {
  if (!observationPoolId.equals(poolState)) reject('observation_pool_binding');
}

function reject(reason: CpSwapValidationReason): never {
  throw new CpSwapValidationError(reason);
}
